import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

/**
 * XIRRComparisonChart - Compares pre-tax and post-tax XIRR
 */
const XIRRComparisonChart = ({ overallResults }) => {
    const prepareData = () => {
        if (!overallResults) return [];

        const preTaxXIRR = overallResults.xirr || 0;
        const postTaxXIRR = overallResults.postTaxXirr || 0;
        const taxImpact = preTaxXIRR - postTaxXIRR;

        return [
            {
                name: 'Pre-Tax XIRR',
                value: preTaxXIRR * 100,
                color: '#667eea'
            },
            {
                name: 'Post-Tax XIRR',
                value: postTaxXIRR * 100,
                color: '#764ba2'
            },
            {
                name: 'Tax Impact',
                value: taxImpact * 100,
                color: '#ef4444'
            }
        ];
    };

    const data = prepareData();

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0];
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
                        {data.payload.name}
                    </p>
                    <p style={{ margin: '0', fontSize: '1.2rem', color: data.payload.color }}>
                        {data.value.toFixed(2)}%
                    </p>
                </div>
            );
        }
        return null;
    };

    if (!overallResults || data.length === 0) {
        return (
            <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#666',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
            }}>
                <p style={{ fontSize: '1.1rem', marginBottom: '8px' }}>📊 No XIRR data available</p>
                <p style={{ fontSize: '0.9rem' }}>Calculate returns to see XIRR comparison</p>
            </div>
        );
    }

    return (
        <div>
            <h3 style={{ marginBottom: '16px', color: '#1a1a2e' }}>📊 XIRR Comparison</h3>
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => `${value.toFixed(1)}%`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>

            {/* Key Insights */}
            <div style={{
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                borderLeft: '4px solid #667eea'
            }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1a1a2e' }}>
                    💡 Key Insights
                </p>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.9rem', color: '#666' }}>
                    <li>Pre-Tax XIRR: {(data[0]?.value || 0).toFixed(2)}%</li>
                    <li>Post-Tax XIRR: {(data[1]?.value || 0).toFixed(2)}%</li>
                    <li>Tax reduces returns by: {(data[2]?.value || 0).toFixed(2)}%</li>
                </ul>
            </div>
        </div>
    );
};

export default XIRRComparisonChart;
